import { Fragment, useEffect, useState } from 'react';
import AgentAvatar from '../common/AgentAvatar';
import styles from './WorkflowDiagram.module.css';

// Keep in sync with the .progressFill animation duration.
const ADVANCE_MS = 4000;

// AgentAvatar key for each stage, by index.
const AVATARS = ['you', 'hunter', 'verifier', 'analyst', 'composer', 'list'];

// One sample lead (Sarah Chen, from the landing-page story) traced through the
// whole pipeline - each stage shows what its agent actually does with her.
const STAGES = [
  {
    name: 'You',
    short: 'the ask',
    tone: 'muted',
    detail: [
      { mark: '❯', text: '“Find me 25 VPs of Ops at Southeast logistics companies.”', sub: 'No filters, no query builder. Just one plain-English sentence.' },
    ],
  },
  {
    name: 'Hunter',
    short: 'finds the people',
    tone: 'accent',
    detail: [
      { mark: '→', text: 'Sarah Chen · VP Ops · Meridian Freight', sub: '+ 24 more matching the titles, industry, size, and territory in your seller profile.' },
    ],
  },
  {
    name: 'Verifier',
    short: 'proves they’re real',
    tone: 'amber',
    detail: [
      { mark: '✓', text: 's.chen@meridianfreight.com', sub: 'Deliverable, checked against the live mail server, not a stale database.' },
      { mark: '✓', text: 'Still VP Ops at Meridian Freight', sub: 'Employment confirmed. She hasn’t moved on since the data was gathered.' },
    ],
  },
  {
    name: 'Analyst',
    short: 'adds the context',
    tone: 'accent',
    detail: [
      { mark: '≡', text: '210 employees · Series B ($34M) · Atlanta HQ', sub: 'Headcount, funding, and industry pulled automatically. Your talking points, ready.' },
    ],
  },
  {
    name: 'Composer',
    short: 'drafts the opener',
    tone: 'amber',
    detail: [
      { mark: '✎', text: '“Saw Meridian’s Series B, congrats. Most ops teams feel the strain past 200 heads…”', sub: 'A personalized opener built from the Analyst’s context. Yours to edit, not to write.' },
    ],
  },
  {
    name: 'Your list',
    short: 'only real leads',
    tone: 'accent',
    detail: [
      { mark: '✓', text: 'Sarah lands verified, enriched, with a draft attached', sub: '18 of 25 made it. The 7 that didn’t never touched your list, or your wallet.' },
    ],
  },
];

export default function WorkflowDiagram() {
  const [active, setActive] = useState(0);

  // Walk the pipeline automatically; clicking a stage jumps there and resets the
  // timer. Reduced motion: no auto-walk, stages still work as tabs.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const t = setTimeout(() => setActive((a) => (a + 1) % STAGES.length), ADVANCE_MS);
    return () => clearTimeout(t);
  }, [active]);

  const stage = STAGES[active];

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <h2 className={styles.heading}>
          This is how your <span className={styles.hl}>agents</span> work together
          <br />
          to get you the <span className={styles.hl}>verified leads</span>
        </h2>

        <div className={styles.pipeline}>
          {STAGES.map((s, i) => (
            <Fragment key={s.name}>
              {i > 0 && (
                <span
                  className={`${styles.link} ${i <= active ? styles.linkDone : ''}`}
                  aria-hidden="true"
                />
              )}
              <button
                type="button"
                className={`${styles.node} ${i === active ? styles.nodeActive : ''} ${i < active ? styles.nodeDone : ''}`}
                onClick={() => setActive(i)}
                aria-current={i === active ? 'step' : undefined}
              >
                <AgentAvatar agent={AVATARS[i]} size={48} className={styles.nodeAvatar} />
                <span className={styles.nodeName}>{s.name}</span>
                <span className={styles.nodeBlurb}>{s.short}</span>
                {i === active && (
                  <span className={styles.progress} aria-hidden="true">
                    <span className={styles.progressFill} />
                  </span>
                )}
              </button>
            </Fragment>
          ))}
        </div>

        {/* key remounts on stage change so the entrance animation replays */}
        <div key={active} className={styles.detail}>
          <span className={`${styles.tag} ${styles[stage.tone]}`}>
            <AgentAvatar agent={AVATARS[active]} size={20} /> {stage.name}
          </span>
          <div className={styles.rows}>
            {stage.detail.map((r) => (
              <div key={r.text} className={styles.dRow}>
                <span className={styles.dMark} aria-hidden="true">{r.mark}</span>
                <span className={styles.dBody}>
                  <span className={styles.dText}>{r.text}</span>
                  <span className={styles.dSub}>{r.sub}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
